import React, { useMemo } from 'react';
import { TextField, CircularProgress, Box } from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useProductForm, ProductItem, Product } from './ProductForm.hooks';
import { DecimalNumberInput } from '../../../ui';
import {
  ProductFormContainer,
  ProductFormHeader,
  ProductRowNumber,
  PieceRangeLabel,
  RemoveButton,
  FormFieldsContainer,
  QuantityContainer,
  StyledAutocomplete,
  ErrorMessage,
  ProductIdentityChipsRow,
  SelectedProductIdentity,
  SelectedProductName,
  ProductCodeChip,
  SupplierCodeChip,
} from './ProductForm.styles';

function trimmedCode(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

const ProductIdentityChips: React.FC<{
  code?: string;
  workshopCode?: string;
  compact?: boolean;
}> = ({ code, workshopCode, compact = false }) => {
  const productCode = trimmedCode(code);
  const supplierCode = trimmedCode(workshopCode);

  if (!productCode && !supplierCode) {
    return null;
  }

  return (
    <ProductIdentityChipsRow sx={compact ? { flexWrap: 'nowrap', flexShrink: 1 } : undefined}>
      {productCode ? (
        <ProductCodeChip
          size="small"
          variant="outlined"
          label={productCode}
          sx={compact ? { maxWidth: '7rem', flexShrink: 0 } : undefined}
        />
      ) : null}
      {supplierCode ? (
        <SupplierCodeChip
          size="small"
          variant="outlined"
          label={`Prov. ${supplierCode}`}
          sx={compact ? { maxWidth: '9rem' } : undefined}
        />
      ) : null}
    </ProductIdentityChipsRow>
  );
};

export interface ProductFormProps {
  item: ProductItem;
  index: number;
  products: Product[];
  canRemove: boolean;
  onRemove: (id: number) => void;
  onUpdate: (id: number, field: keyof ProductItem, value: string) => void;
  selectedProductIds?: number[];
  disabled?: boolean;
  /** Kept for compatibility with parent props; not used for product filtering. */
  supplierId?: string;
  /**
   * Sin numeración de fila ni caja/borde propios — para usarlo suelto dentro de un contenedor
   * ajeno (p. ej. la barra "Agregar producto"), en vez de como una fila más de una grilla.
   */
  bare?: boolean;
}

const ProductForm: React.FC<ProductFormProps> = ({
  item,
  index,
  products,
  canRemove,
  onRemove,
  onUpdate,
  selectedProductIds = [],
  disabled = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  supplierId,
  bare = false,
}) => {
  const {
    availableProducts,
    handleProductSelect,
    handleQuantityChange,
    handleRemove,
    isValid,
    showQuantityError,
    hasAvailableProducts,
    selectedProduct,
    isCurrentProductAvailable,
    searchInput,
    setSearchInput,
    asyncLoading,
    asyncHasMore,
    asyncLoadMore,
    useAsyncSearch,
  } = useProductForm({
    item,
    index,
    products,
    canRemove,
    onRemove,
    onUpdate,
    selectedProductIds,
    useAsyncSearch: true,
  });

  // Ensure selected product is always in the list
  const productsWithSelected = useMemo(() => {
    if (!selectedProduct) {
      return availableProducts;
    }

    const hasActiveSearch = useAsyncSearch && searchInput.trim().length > 0;
    if (hasActiveSearch) {
      // While searching, do not force-insert current selection into options.
      // This avoids re-selecting the previous item when user tries to pick another one.
      return availableProducts;
    }

    // Check if selected product is already in the list (by ID, not reference)
    const isInList = availableProducts.some((p) => p.id === selectedProduct.id);

    if (isInList) {
      // If it's in the list, return as-is (will use reference from availableProducts)
      return availableProducts;
    }

    // If not in list, add it at the beginning
    return [selectedProduct, ...availableProducts];
  }, [availableProducts, selectedProduct, useAsyncSearch, searchInput]);

  // Fix: Ensure selectedProduct matches an option in productsWithSelected for reference equality
  const displayValue = useMemo(() => {
    if (!selectedProduct) return null;

    const productFromList = productsWithSelected.find((p) => p.id === selectedProduct.id);
    const base = productFromList ?? selectedProduct;
    const workshopCode =
      base.workshopCode?.trim() ||
      selectedProduct.workshopCode?.trim() ||
      item.workshopCode?.trim() ||
      undefined;

    if (workshopCode === base.workshopCode) {
      return base;
    }

    return { ...base, workshopCode };
  }, [selectedProduct, productsWithSelected, item.workshopCode]);

  const pieceCount = Math.trunc(Number(item.quantity));
  const hasValidPieceCount = Number.isFinite(pieceCount) && pieceCount >= 1;
  const pieceRangeLabel = !hasValidPieceCount
    ? null
    : pieceCount === 1
      ? 'Pieza 1'
      : `Piezas 1–${pieceCount}`;

  const showSelectedIdentity = Boolean(displayValue && searchInput.trim() === '');

  const Wrapper = bare ? React.Fragment : ProductFormContainer;

  return (
    <Wrapper>
      {!bare && (
        <ProductFormHeader>
          <ProductRowNumber>{index + 1}.</ProductRowNumber>
          {pieceRangeLabel && <PieceRangeLabel>{pieceRangeLabel}</PieceRangeLabel>}
        </ProductFormHeader>
      )}

      <FormFieldsContainer>
        {/* Product Selection */}
        <StyledAutocomplete
          options={productsWithSelected}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.name;
          }}
          isOptionEqualToValue={(option, value) => {
            if (!value) return false;
            return option.id === value.id;
          }}
          value={displayValue}
          onChange={(_, newValue) => {
            handleProductSelect(newValue);
            if (useAsyncSearch) {
              // After selecting, clear query so next open/search starts clean.
              setSearchInput('');
            }
          }}
          onInputChange={(_, newInputValue, reason) => {
            if (!useAsyncSearch) return;
            if (reason === 'input') {
              // If there is already a selected product, typing means user wants to replace it.
              // Clear current selection first so old value doesn't get re-applied.
              if (selectedProduct) {
                handleProductSelect(null);
              }
              setSearchInput(newInputValue);
            } else if (reason === 'clear') {
              handleProductSelect(null);
              setSearchInput('');
            }
            // Intentionally ignore 'reset' to avoid setting search to selected label.
          }}
          loading={asyncLoading}
          filterOptions={(options) => (useAsyncSearch ? options : options)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Producto"
              placeholder={
                showSelectedIdentity
                  ? ''
                  : useAsyncSearch
                    ? 'Buscar por nombre, código o código proveedor...'
                    : 'Seleccionar producto'
              }
              required
              error={!isCurrentProductAvailable && item.productName !== ''}
              helperText={
                !isCurrentProductAvailable && item.productName !== ''
                  ? 'Producto no disponible'
                  : useAsyncSearch && searchInput
                    ? 'Escribe para buscar productos...'
                    : ''
              }
              InputLabelProps={{ shrink: showSelectedIdentity ? true : undefined }}
              disabled={disabled}
              inputProps={{
                ...params.inputProps,
                // Sin padding: 0 acá — el input real (invisible cuando hay producto seleccionado)
                // sigue sosteniendo el alto del campo con su padding normal; si lo anulamos, el
                // campo se encoge porque lo que se ve en su lugar (nombre + chips) no tiene alto
                // propio para compensar.
                style: showSelectedIdentity
                  ? { ...params.inputProps?.style, width: 0, minWidth: 0, opacity: 0 }
                  : params.inputProps?.style,
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment:
                  showSelectedIdentity && displayValue ? (
                    <React.Fragment>
                      {params.InputProps.startAdornment}
                      <SelectedProductIdentity>
                        <SelectedProductName>{displayValue.name}</SelectedProductName>
                        <ProductIdentityChips
                          code={displayValue.code}
                          workshopCode={displayValue.workshopCode}
                          compact
                        />
                      </SelectedProductIdentity>
                    </React.Fragment>
                  ) : (
                    params.InputProps.startAdornment
                  ),
                endAdornment: (
                  <React.Fragment>
                    {asyncLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25, minWidth: 0 }}
              >
                <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                <ProductIdentityChips code={option.code} workshopCode={option.workshopCode} />
              </Box>
            </li>
          )}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                },
              ],
            },
          }}
          ListboxProps={{
            style: { maxHeight: '300px' },
            onScroll: (event) => {
              if (!useAsyncSearch || !asyncHasMore || asyncLoading) return;
              const listboxNode = event.currentTarget as HTMLElement;
              const nearBottom =
                listboxNode.scrollHeight - listboxNode.scrollTop - listboxNode.clientHeight < 80;
              if (nearBottom) {
                asyncLoadMore();
              }
            },
          }}
          disabled={disabled || (!hasAvailableProducts && !useAsyncSearch && !selectedProduct)}
          noOptionsText={
            asyncLoading
              ? 'Buscando productos...'
              : searchInput && useAsyncSearch
                ? 'No se encontraron productos'
                : hasAvailableProducts
                  ? 'No hay productos disponibles'
                  : 'No hay productos para seleccionar'
          }
        />

        {/* Quantity with Remove Button */}
        <QuantityContainer>
          <DecimalNumberInput
            label="Cant."
            value={item.quantity}
            onChange={(val) => handleQuantityChange(val != null ? String(Math.trunc(val)) : '')}
            required
            error={showQuantityError}
            helperText={showQuantityError ? 'Cantidad debe ser mayor a 0' : ''}
            disabled={disabled}
            decimalPlaces={0}
            min={1}
            fullWidth
          />
          {canRemove && (
            <RemoveButton onClick={handleRemove} aria-label="Remove product" size="small">
              <DeleteIcon fontSize="small" />
            </RemoveButton>
          )}
        </QuantityContainer>
      </FormFieldsContainer>

      {/* Validation Messages */}
      {!isValid && item.productName && item.quantity && (
        <ErrorMessage>Verifica que todos los campos estén completos</ErrorMessage>
      )}
    </Wrapper>
  );
};

export default ProductForm;
